<template>
<div
    ref="targetContainerRef"
    class="scalable-element map__buttons"
    :class="{ isFullscreen }"
>
  <div class="scalable-element__controls">
    <q-btn
        v-if="scalable"
        :disable="scaleIsMaxedOut"
        icon="add"
        dense
        color="white"
        text-color="black"
        @click="zoomIn"
    />
    <q-btn
        v-if="scalable"
        :disable="scaleIsBottomedOut"
        icon="remove"
        dense
        color="white"
        text-color="black"
        @click="zoomOut"
    />
    <q-btn
        :icon="isFullscreen ? 'fullscreen_exit' : 'fullscreen'"
        dense
        color="white"
        text-color="black"
        @click="toggleFullscreen"
    />
  </div>
  <slot />
</div>
</template>
<script setup>
import { computed, ref } from 'vue'
import {
  onKeyStroke,
  useEventListener,
  useFocusWithin,
  useMouseInElement,
} from '@vueuse/core'

const props = defineProps({
  maxZoom: { type: Number, default: 4 },
  zoomStep: { type: Number, default: 0.2 },
  minZoom: { type: Number, default: 0.3 },
  scalable: { type: Boolean, default: true },
})

const targetContainerRef = ref()
const scale = defineModel('scale')
const scaleIsMaxedOut = computed(() => scale.value >= props.maxZoom)
const scaleIsBottomedOut = computed(() => scale.value <= props.minZoom)
const isFullscreen = ref(false)
const zoomIn = () => {
  if (!props.scalable || scaleIsMaxedOut.value) {
    return
  }
  scale.value += props.zoomStep
}
const zoomOut = () => {
  if (!props.scalable || scaleIsBottomedOut.value) {
    return
  }
  scale.value -= props.zoomStep
}

const toggleFullscreen = () => {
  if (isFullscreen.value) {
    document.exitFullscreen()
  } else {
    targetContainerRef.value.requestFullscreen()
  }
}

useEventListener(document, 'fullscreenchange', () => {
  isFullscreen.value = !!document.fullscreenElement
})

const hasFocus = useFocusWithin(targetContainerRef)
const mouseInElement = useMouseInElement(targetContainerRef)
onKeyStroke(['=', '+', '_', '-'], (event) => {
  if (
      !props.scalable ||
      !event.ctrlKey ||
      (!hasFocus.focused.value && !isFullscreen.value)
  ) {
    return
  }
  event.preventDefault()
  if (['=', '+'].includes(event.key)) {
    return zoomIn()
  }
  return zoomOut()
})

useEventListener(
    document,
    'wheel',
    (event) => {
      if (
          !props.scalable ||
          !event.ctrlKey ||
          (!hasFocus.focused.value &&
              mouseInElement.isOutside.value &&
              !isFullscreen.value)
      ) {
        return
      }
      event.preventDefault()
      if (event.deltaY > 0) {
        zoomOut()
      } else {
        zoomIn()
      }
    },
    { passive: false },
)
</script>

<style lang="scss">
.scalable-element {
  width: 100%;
  height: 100%;

  &__controls {
    position: absolute;
    z-index: 1000;
    z-index: 5;
    bottom: 0;
    right: 40px;

    .q-btn {
      margin-left: 10px;

      .q-icon {
        font-size: 1.715em;
      }
    }
  }

  &.isFullscreen {
    .scalable-element {
      &__controls {
        bottom: 15px;
        right: 25px;
      }
    }
  }
}
</style>
